import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import feather from 'feather-icons';

import { NavbarComponent } from '../../components/navbar/navbar.component';
import { FooterComponent } from '../../components/footer/footer.component';
import { SwitcherComponent } from '../../components/switcher/switcher.component';
import { IdScannerComponent } from '../../components/id-scanner/id-scanner.component';

import { CheckInService, IdScanResult, CheckInStep } from '../../services/check-in.service';
import { CheckInData, BookingData } from '../../interfaces/booking.interface';

@Component({
  selector: 'app-check-in',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NavbarComponent,
    FooterComponent,
    SwitcherComponent,
    IdScannerComponent
  ],
  templateUrl: './check-in.component.html',
  styleUrl: './check-in.component.scss'
})
export class CheckInComponent implements OnInit, OnDestroy, AfterViewInit {
  private destroy$ = new Subject<void>();

  currentStep = 1;
  steps: CheckInStep[] = [];
  checkInData: CheckInData | null = null;
  verifiedBooking: Partial<BookingData> | null = null;

  // Forms
  bookingVerificationForm: FormGroup;
  guestConfirmationForm: FormGroup;

  // Document scanning
  selectedDocumentType: 'passport' | 'national_id' = 'passport';

  // Loading states
  isVerifyingBooking = false;
  isGeneratingAccess = false;

  // Messages
  verificationMessage = '';
  verificationMessageType: 'success' | 'error' | 'info' = 'info';

  constructor(
    private fb: FormBuilder,
    private checkInService: CheckInService,
    private route: ActivatedRoute
  ) {
    this.bookingVerificationForm = this.fb.group({
      bookingId: ['', [Validators.required, Validators.minLength(5)]],
      email: ['', [Validators.required, Validators.email]]
    });

    this.guestConfirmationForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phone: ['', Validators.required],
      tripReason: ['private', Validators.required],
      additionalGuests: ['']
    });
  }

  ngOnInit(): void {
    this.steps = this.checkInService.getCheckInSteps();

    // Check for booking ID in URL parameters
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['bookingId']) {
        this.bookingVerificationForm.patchValue({
          bookingId: params['bookingId']
        });
      }
    });

    // Check for booking ID in query parameters
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(queryParams => {
      if (queryParams['bookingId']) {
        this.bookingVerificationForm.patchValue({
          bookingId: queryParams['bookingId']
        });
      }
      if (queryParams['email']) {
        this.bookingVerificationForm.patchValue({
          email: queryParams['email']
        });
      }
    });

    // Subscribe to current step changes
    this.checkInService.currentStep$
      .pipe(takeUntil(this.destroy$))
      .subscribe(step => {
        this.currentStep = step;
        this.steps = this.checkInService.getCheckInSteps();
      });

    // Subscribe to check-in data changes
    this.checkInService.checkInData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.checkInData = data;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      feather.replace();
    }, 100);
  }

  // Step 1: Booking Verification
  verifyBooking(): void {
    if (this.bookingVerificationForm.invalid) {
      this.bookingVerificationForm.markAllAsTouched();
      return;
    }

    const { bookingId, email } = this.bookingVerificationForm.value;
    this.isVerifyingBooking = true;
    this.verificationMessage = '';

    this.checkInService.verifyBooking(bookingId, email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.isVerifyingBooking = false;

          if (result.success && result.booking) {
            this.verifiedBooking = result.booking;
            this.verificationMessage = result.message;
            this.verificationMessageType = 'success';

            // Pre-fill guest confirmation form
            if (result.booking.guestDetails) {
              this.guestConfirmationForm.patchValue({
                firstName: result.booking.guestDetails.firstName,
                lastName: result.booking.guestDetails.lastName,
                phone: result.booking.guestDetails.phone
              });
            }

            // Move to next step after a short delay
            setTimeout(() => {
              this.checkInService.nextStep();
            }, 1500);
          } else {
            this.verificationMessage = result.message;
            this.verificationMessageType = 'error';
          }
        },
        error: (error) => {
          this.isVerifyingBooking = false;
          this.verificationMessage = 'An error occurred while verifying your booking. Please try again.';
          this.verificationMessageType = 'error';
        }
      });
  }

  // Step 2: ID Scanning
  onDocumentTypeChange(type: 'passport' | 'national_id'): void {
    this.selectedDocumentType = type;
  }

  onIdScanComplete(result: IdScanResult): void {
    if (result.success) {
      // Auto-fill guest form with scanned data if available
      if (result.extractedData) {
        this.guestConfirmationForm.patchValue({
          firstName: result.extractedData.firstName || this.guestConfirmationForm.value.firstName,
          lastName: result.extractedData.lastName || this.guestConfirmationForm.value.lastName
        });
      }

      // Move to next step
      setTimeout(() => {
        this.checkInService.nextStep();
      }, 2000);
    }
  }

  onIdScanError(error: string): void {
    console.error('ID Scan Error:', error);
  }

  // Step 3: Guest Information Confirmation
  confirmGuestInformation(): void {
    if (this.guestConfirmationForm.invalid) {
      this.guestConfirmationForm.markAllAsTouched();
      return;
    }

    // Update check-in data with trip reason
    const currentCheckIn = this.checkInService.getCurrentCheckInData();
    if (currentCheckIn) {
      const updatedCheckIn = {
        ...currentCheckIn,
        tripReason: this.guestConfirmationForm.value.tripReason,
        updatedAt: new Date()
      };
      this.checkInService.updateCheckInData(updatedCheckIn);
    }

    // Move to final step
    this.checkInService.nextStep();
  }

  // Step 4: Generate Access Code
  generateAccessCode(): void {
    this.isGeneratingAccess = true;

    this.checkInService.generateAccessCode()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.isGeneratingAccess = false;

          if (result.success) {
            this.checkInService.completeCheckIn();
          }
        },
        error: (error) => {
          this.isGeneratingAccess = false;
          console.error('Error generating access code:', error);
        }
      });
  }

  // Navigation
  goToStep(step: number): void {
    if (step <= this.currentStep || this.steps[step - 1].completed) {
      this.checkInService.setCurrentStep(step);
    }
  }

  previousStep(): void {
    this.checkInService.previousStep();
  }

  nextStep(): void {
    this.checkInService.nextStep();
  }

  // Utility methods
  isStepAccessible(step: number): boolean {
    return step <= this.currentStep || this.steps[step - 1].completed;
  }

  resetCheckIn(): void {
    this.checkInService.resetCheckIn();
    this.bookingVerificationForm.reset();
    this.guestConfirmationForm.reset();
    this.verifiedBooking = null;
    this.verificationMessage = '';
  }

  formatDate(date: Date | null | undefined): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  formatTime(time: string): string {
    return time;
  }
}
